import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type InputHTMLAttributes,
} from "react";

import { formatNumberInput, parseFormattedNumber } from "@/utils/formatUtils";

type NativeInputProps = InputHTMLAttributes<HTMLInputElement>;

interface NumberInputProps extends Omit<NativeInputProps, "value" | "onChange" | "type"> {
  /** Canonical decimal string ("1234.56"). Empty string when blank. */
  value: string | null | undefined;
  /** Emits the next canonical decimal string. */
  onChange: (next: string) => void;
}

/**
 * Controlled <input> for amounts/percentages in Spanish locale ("1.234,56").
 *
 * Stores the canonical decimal ("1234.56") in form state, displays the
 * Spanish-formatted version, and restores the caret after every keystroke
 * so reformatting doesn't kick the cursor to the end.
 *
 * Cursor restoration measures offset from the end of the typed string:
 * stable across thousand-separator shifts since the decimal portion lives
 * on the right.
 */
const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onChange, inputMode, ...rest },
  externalRef,
) {
  const localRef = useRef<HTMLInputElement | null>(null);
  const pendingCursorOffset = useRef<number | null>(null);

  const setRefs = useCallback(
    (el: HTMLInputElement | null) => {
      localRef.current = el;
      if (typeof externalRef === "function") {
        externalRef(el);
      } else if (externalRef) {
        externalRef.current = el;
      }
    },
    [externalRef],
  );

  const displayValue = formatNumberInput(value ?? "");

  useEffect(() => {
    const offset = pendingCursorOffset.current;
    if (offset === null) return;
    pendingCursorOffset.current = null;

    const el = localRef.current;
    if (!el) return;
    if (document.activeElement !== el) return;

    const newPos = Math.max(0, el.value.length - offset);
    el.setSelectionRange(newPos, newPos);
  }, [displayValue]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const typedValue = input.value;
    const cursorPos = input.selectionStart ?? typedValue.length;

    pendingCursorOffset.current = typedValue.length - cursorPos;
    onChange(parseFormattedNumber(typedValue));
  };

  return (
    <input
      ref={setRefs}
      type="text"
      inputMode={inputMode ?? "decimal"}
      value={displayValue}
      onChange={handleChange}
      {...rest}
    />
  );
});

export default NumberInput;
